import { AsyncPipe, SlicePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  DisplayUser,
  GalleryDto,
  GalleryGroupAccessLevel,
  GalleryInvitation,
  GalleryMemberAccess,
  GalleryMemberDto,
  GalleryPublicAccessLevel,
  GalleryTenantAccessLevel,
  GroupDto,
  MemberDto,
  TenantDto,
} from '@picflow/types';
import { Observable, Subscription, delay, first, firstValueFrom, forkJoin, from, map, of, switchMap, tap } from 'rxjs';
import {
  EmailsEntryFieldComponent,
  EmailsEntryFieldComponent as EmailsEntryFieldComponent_1,
} from '../../../account-gallery-shared/components/emails-entry-field/emails-entry-field.component';
import { debounce } from '../../../lib/debounce';
import { getPattern } from '../../../lib/get-pattern';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { AccessLevelService } from '../../../services/access-level.service';
import { DataLoaderService } from '../../../services/data-loader.service';
import { GalleryMemberService } from '../../../services/gallery-member.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { TenantService } from '../../../services/tenant.service';
import { ToastService } from '../../../services/toast.service';
import { UserGroupService } from '../../../services/user-group.service';
import { DisplayUserEmailDto, VisitorService, visitorResponse } from '../../../services/visitor.service';
import { GalleryShareContextComponent } from '../../../shared-extra/components/gallery-share-context/gallery-share-context.component';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { RadioButtonComponent } from '../../../shared-extra/components/radio-button/radio-button.component';
import { AvatarPipe } from '../../../shared-extra/pipes/avatar.pipe';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';
import { CustomCurrencyPipe } from '../../../shared/pipes/custom-currency.pipe';

@Component({
  selector: 'app-share-gallery-collaborators',
  templateUrl: './share-gallery-collaborators.component.html',
  styleUrls: ['./share-gallery-collaborators.component.scss'],
  standalone: true,
  imports: [
    LoadingIndicatorComponent,
    EmailsEntryFieldComponent_1,
    GalleryShareContextComponent,
    SvgComponent,
    ButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    TransitionPreventorDirective,
    InputFieldComponent,
    AsyncPipe,
    CapitalizePipe,
    AvatarPipe,
    ContextMenuComponent,
    RadioButtonComponent,
    SlicePipe,
    CustomCurrencyPipe,
    AvatarComponent,
  ],
})
export class ShareGalleryCollaboratorsComponent implements OnInit, OnDestroy {
  @Input() gallery: GalleryDto;
  @Output() onChangeInviteMode = new EventEmitter<null | 'email' | 'choice'>();
  tenant: TenantDto;
  tenantAccessLevel: GalleryTenantAccessLevel;
  tenantAccessLevels: GalleryTenantAccessLevel[] = ['edit', 'review', 'view_only', 'no_access'];
  groupAccessLevels: (GalleryGroupAccessLevel | 'remove')[] = ['edit', 'review', 'view_only', 'remove'];
  accessLevel: GalleryGroupAccessLevel | GalleryPublicAccessLevel | GalleryTenantAccessLevel;
  galleryMembers: GalleryMemberDto[];
  parentGalleryMembers: GalleryMemberDto[];
  pendingGalleryMembers: GalleryInvitation[] = [];
  owner: DisplayUser;
  memberCount: number;
  members: MemberDto[];
  visitors: visitorResponse[];
  allGalleryMembers: GalleryMemberDto[];
  allGalleryInvites: GalleryInvitation[];
  userGroups: GroupDto[] = [];
  userGroupUserCounts: { [groupId: string]: number } = {};
  galleryUserGroups: {
    group: string;
    id: string;
    access: GalleryGroupAccessLevel;
  }[] = [];
  galleryUserGroupsMap: { [groupId: string]: boolean } = {};
  isGalleryGuest: boolean;
  inviteAccessLevel: GalleryTenantAccessLevel = 'edit';
  inviteAccessLevels: GalleryTenantAccessLevel[] = ['edit', 'review', 'view_only'];
  memberAccessLevels: (GalleryTenantAccessLevel | 'remove')[] = ['edit', 'review', 'view_only', 'remove'];
  memberMeAccessLevels: (GalleryTenantAccessLevel | 'leave')[] = ['leave'];
  loading = true;
  updateSub: Subscription;
  inviteMode: null | 'email' | 'choice' = null;
  inviteType: 'gallery' | 'team' = 'gallery';
  autoCompleteMode = false;
  autoCompleteResults: { id: string; email: string; first_name?: string; last_name?: string; isInvite?: boolean }[] =
    [];
  emailPattern = getPattern('email');
  sendingInvites = false;
  messageForm = new FormGroup({
    notify: new FormControl(true),
    message: new FormControl(),
  });
  messageTyped = false;
  @ViewChild('emailsEntryField', { static: false }) emailsEntryField: EmailsEntryFieldComponent;

  nonMemberEmails: string[] = [];
  emailsCached: string[] = [];

  constructor(
    public state: StateService,
    private tenantService: TenantService,
    private galleryService: GalleryService,
    private accessLevelService: AccessLevelService,
    private galleryMemberService: GalleryMemberService,
    private loader: DataLoaderService,
    private router: Router,
    private toasts: ToastService,
    private userGroupService: UserGroupService,
    private permissionService: PermissionService,
    private visitorService: VisitorService
  ) {}

  private analytics = inject(PICFLOW_ANALYTICS);

  async ngOnInit() {
    const tenantId = this.getTenantId();
    this.tenant = this.state.tenants.find(t => t.id === tenantId);

    this.updateSub = this.galleryService.onUpdates.subscribe(event => {
      if (!event) return;
      if (event[1] !== this.gallery.id) return;
      if (event[0] === 'updated') {
        const tenant = this.gallery.tenant as TenantDto;
        this.gallery = Object.assign(this.gallery, event[2]);
        this.gallery.tenant = tenant;
        this.refreshDataDebounced();
      }
    });
    await this.refreshData();
    this.loading = false;
  }

  private getMembers() {
    if (this.tenant && this.state.tenants.find(t => t.id === this.tenant.id)) {
      return this.tenantService.getMembers();
    }
    return of([]);
  }

  private async getUserGroups() {
    if (!this.permissionService.canDisplayElement('groups.list')) return [];
    return this.userGroupService.getUserGroups();
  }

  private async getGalleryUserGroups() {
    if (!this.permissionService.canDisplayElement('groups.list')) return [];
    return this.userGroupService.getGalleryUserGroups(this.gallery.id);
  }

  private getGalleryMembers(): Observable<GalleryMemberDto[][]> {
    if (!this.gallery.section) {
      return this.galleryMemberService.getGalleryMembers(this.gallery.id).pipe(map(members => [members, []]));
    }

    return forkJoin([
      this.galleryMemberService.getGalleryMembers(this.gallery.id).pipe(first()),
      this.galleryMemberService.getGalleryMembers(this.gallery['parent_gallery_id']).pipe(first()),
    ]);
  }

  private getTenantId() {
    if (typeof this.gallery['tenant'] === 'string') {
      return this.gallery['tenant'];
    }
    if ((this.gallery['tenant'] as TenantDto)?.id) return this.gallery['tenant'].id;
    if (this.gallery['tenant']) return this.gallery['tenant'];
    console.warn('Gallery doesnt contain a tenant ID');

    return this.state.activeTenant.id;
  }

  private async refreshData() {
    const [owner, members, galleryMembers, isGalleryGuest, accessLevels, accessLevel] = await Promise.all([
      this.galleryService.getGalleryOwner(this.gallery.id),
      firstValueFrom(this.getMembers()),
      firstValueFrom(this.getGalleryMembers()),
      firstValueFrom(this.accessLevelService.isGalleryGuest(this.gallery)),
      this.accessLevelService.getAccessLevels(this.gallery),
      this.accessLevelService.getAccessLevel(this.gallery),
    ]);
    this.tenantAccessLevel = accessLevels[0];

    this.accessLevel = accessLevel;
    this.galleryMembers = galleryMembers[0];
    this.parentGalleryMembers = galleryMembers[1];
    this.owner = owner;
    this.memberCount = members.length;

    this.members = members;
    this.isGalleryGuest = isGalleryGuest;

    const me = this.galleryMembers.find(m => m.user['id'] === this.state.user?.id);
    if (me) {
      this.memberMeAccessLevels = [me.access, 'leave'];
    }

    if (this.isGalleryGuest) {
      this.tenant = this.gallery.tenant as TenantDto;

      this.galleryMembers.sort(a => {
        if (a.user['id'] !== this.state.user.id) return 1;
        return -1;
      });

      this.parentGalleryMembers.sort(a => {
        if (a.user['id'] !== this.state.user.id) return 1;
        return -1;
      });
    }
    this.loading = false;

    if (this.isGalleryGuest) {
      this.pendingGalleryMembers = [];
    } else {
      const [pendingGalleryMembers, userGroups, galleryUserGroups] = await Promise.all([
        firstValueFrom(this.getPendingGalleryMembers()),
        this.getUserGroups(),
        this.getGalleryUserGroups(),
      ]);

      this.pendingGalleryMembers = pendingGalleryMembers;
      this.userGroups = userGroups;
      this.galleryUserGroups = galleryUserGroups;
      this.galleryUserGroupsMap = {};
      this.galleryUserGroups.forEach(g => {
        this.galleryUserGroupsMap[g.group] = true;
        g['groupData'] = this.userGroups.find(ug => ug.id === g.group);
      });
      this.getUserGroupUserCounts(this.galleryUserGroups.map(g => g['groupData']));
    }
  }

  private getVisitors(): Observable<visitorResponse[]> {
    const tenantId = (this.gallery.tenant as TenantDto)?.id || (this.gallery.tenant as string);
    if (!this.state.tenants.find(tenant => tenant.id === tenantId)) return of([]);
    return this.visitorService.getAllVisitors();
  }

  getPendingGalleryMembers() {
    if (this.accessLevel !== 'edit') return of([]);
    return this.galleryMemberService.getPendingGalleryMembers(this.gallery.id);
  }

  refreshDataDebounced = debounce(() => {
    this.refreshData();
  }, 1000);

  async changeTenantAccess(accessLevel: GalleryTenantAccessLevel) {
    this.gallery.tenant_access = accessLevel;
    this.tenantAccessLevel = accessLevel;
    await this.galleryService.updateGallery(this.gallery.id, { tenant_access: accessLevel });
    this.accessLevel = await this.accessLevelService.getAccessLevel(this.gallery);
  }

  async changeGroupAccess(
    group: { group: string; id: string; access: GalleryGroupAccessLevel },
    accessLevel: GalleryGroupAccessLevel | 'remove'
  ) {
    if (accessLevel === 'remove') {
      await this.userGroupService.removeUserGroupFromGallery(group.group, this.gallery.id);
      this.galleryUserGroups = this.galleryUserGroups.filter(g => g.id !== group.id);
      delete this.galleryUserGroupsMap[group.group];
    } else {
      await this.userGroupService.updateUserGroupAccessLevel(group.id, accessLevel);
      group.access = accessLevel;
    }
  }

  async changeMemberAccess(member: GalleryMemberDto, accessLevel: GalleryMemberAccess | 'remove' | 'leave') {
    if (accessLevel === 'remove') {
      try {
        await firstValueFrom(this.galleryMemberService.removeMember(member.id));
        this.galleryMembers = await firstValueFrom(this.galleryMemberService.getGalleryMembers(this.gallery.id));
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 404 && err.status !== 403) throw err;
        // Member was already removed
      }
    } else if (accessLevel === 'leave') {
      try {
        await firstValueFrom(this.galleryMemberService.removeMember(member.id));
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 404 && err.status !== 403) throw err;
        // User was already removed
      }
      this.loader.removeFuzzy('galleries');
      this.router.navigate(['/a', 'galleries']);
      // this.close();
    } else {
      if (member.access !== accessLevel) {
        member.access = accessLevel;
        try {
          await firstValueFrom(this.galleryMemberService.updateMember(member.id, accessLevel));
          if (member.id === this.state.user.id) {
            this.accessLevel = await this.accessLevelService.getAccessLevel(this.gallery);
          }
        } catch (err) {
          if (!(err instanceof HttpErrorResponse)) throw err;
          if (err.status !== 404 && err.status !== 403) throw err;
          this.toasts.add(`Can't find member`, 'error');
          await this.refreshData();
        }
      }
    }
  }

  async changePendingMemberAccess(invite: GalleryInvitation, accessLevel: GalleryMemberAccess | 'remove') {
    if (accessLevel === 'remove') {
      try {
        await firstValueFrom(this.galleryMemberService.removeInvite(invite.id));
        this.pendingGalleryMembers = await firstValueFrom(
          this.galleryMemberService.getPendingGalleryMembers(this.gallery.id)
        );
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 404 && err.status !== 403) throw err;
        // Invite was already removed
      }
    } else {
      invite.access = accessLevel;
      try {
        await firstValueFrom(this.galleryMemberService.updateInvite(invite.id, accessLevel));
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 404 && err.status !== 403) throw err;
        // either membership was accepted or deleted
        await this.refreshData();
        const member = this.galleryMembers.find(
          m => member.user['email']?.toLowerCase() === (m.user as DisplayUserEmailDto).email?.toLowerCase()
        );
        if (!member) return; // Invite was deleted
        // Invite was accepted
        await this.changeMemberAccess(member, accessLevel);
      }
    }
  }

  cancelInvite() {
    this.inviteMode = null;
    this.onChangeInviteMode.emit(null);
    this.messageForm.setValue({
      message: '',
      notify: true,
    });
    this.emailsEntryField.form.setValue({ emails: '' });
    this.messageTyped = false;
    this.emailsEntryField.emails = [];
  }

  cancelInviteChoice() {
    this.inviteMode = null;
    this.onChangeInviteMode.emit(null);
    this.nonMemberEmails = [];
  }

  async onEmailChange([inviteMode, emailValue]: [boolean, string]) {
    this.inviteMode = inviteMode ? 'email' : null;
    this.onChangeInviteMode.emit(this.inviteMode);
    if (!this.visitors) {
      const [visitors, allGalleryMembers, allGalleryInvites] = await Promise.all([
        firstValueFrom(this.getVisitors()),
        firstValueFrom(this.galleryMemberService.getAllGalleryMembers(true)),
        firstValueFrom(this.galleryMemberService.getAllPendingGalleryMembers()),
      ]);

      this.visitors = visitors;
      this.allGalleryMembers = allGalleryMembers;
      this.allGalleryInvites = allGalleryInvites;
    }
    this.autoCompleteResults = [
      ...this.members.filter(member => {
        return this.autoCompleteCheckMember(member, emailValue);
      }),
      ...this.allGalleryMembers
        .map(member => <DisplayUserEmailDto>member.user)
        .filter(member => {
          if (this.members.some(m => m.email === member.email)) return false;
          return this.autoCompleteCheckMember(member, emailValue);
        }),
      ...this.allGalleryInvites
        .filter(invite => {
          if (this.members.some(m => m.email === invite.email)) return false;
          if (this.allGalleryMembers.some(m => m.user['email'] === invite.email)) return false;
          return this.autoCompleteCheckMember(invite, emailValue);
        })
        .map(invite => {
          return {
            ...invite,
            isInvite: true,
          };
        }),
      ...this.visitors
        .map(r => {
          if (r.user) {
            const user = r.user;
            return {
              id: user.id,
              email: user['email'],
              first_name: user['first_name'],
              last_name: user['last_name'],
            };
          }
          if (r.guest) {
            const guest = r.guest;
            const nameParts = guest.name.split(' ');
            const lastName = nameParts.pop();
            const firstName = nameParts.join(' ');
            return {
              id: guest.id,
              email: guest['email'],
              first_name: lastName,
              last_name: firstName,
            };
          }
        })
        .filter(visitor => {
          if (!visitor) return false;
          if (!visitor.email) return false;
          if (this.members.some(m => m.email === visitor.email)) return false;
          if (this.allGalleryMembers.some(m => m.user['email'] === visitor.email)) return false;
          if (this.allGalleryInvites.some(m => m.email === visitor.email)) return false;
          return this.autoCompleteCheckMember(visitor, emailValue);
        }),
    ];

    this.autoCompleteMode = this.autoCompleteResults.length > 0;
  }

  setValue(email: string) {
    this.emailsEntryField.form.setValue({ emails: email });
  }

  onFocus() {
    if (!this.emailsEntryField) return;
    this.autoCompleteResults = this.members.filter(member => {
      return this.autoCompleteCheckMember(member, this.emailsEntryField.form.value.emails);
    });
    this.autoCompleteMode = this.autoCompleteResults.length > 0;
  }

  onBlur() {
    this.autoCompleteMode = false;
    this.autoCompleteResults = [];
  }

  autoComplete() {
    return (email: string) => {
      return this.autoCompleteResults[0]?.email || email;
    };
  }

  autoCompleteCheckMember(
    member: { id: string; email: string; first_name?: string; last_name?: string },
    emailValue: string
  ) {
    if (!emailValue) return false;
    if (this.galleryMembers.find(_m => _m.user['id'] === member.id)) return false;
    if (member.id === this.owner.id) return false;
    if (member.id === this.state.user.id) return false;
    const first_name = member.first_name?.toLowerCase() || '';
    const last_name = member.last_name?.toLowerCase() || '';
    const email = member['email'].toLowerCase();
    const value = emailValue.toLowerCase();
    if (this.emailsEntryField.emails.find(e => e.toLowerCase() === email)) return false;
    if (`${first_name} ${last_name}`.includes(value)) return true;
    if (email.includes(value)) return true;
    return false;
  }

  validateEmail(): (email: string) => boolean {
    return (email: string) => {
      email = email?.toLowerCase();
      if (this.galleryMembers.find(m => m.user['email']?.toLowerCase() === email)) return false;
      if (this.parentGalleryMembers.find(m => m.user['email']?.toLowerCase() === email)) return false;
      if (this.pendingGalleryMembers.find(m => m.email?.toLowerCase() === email)) return false;
      if (this.owner['email']?.toLowerCase() === email) return false;
      return true;
    };
  }

  getEmailErrorMessage(): (email: string) => string {
    return (email: string) => {
      email = email?.toLowerCase();
      if (this.galleryMembers.find(m => m.user['email']?.toLowerCase() === email)) {
        return 'This person has already been invited to this gallery.';
      }
      if (this.pendingGalleryMembers.find(m => m.email?.toLowerCase() === email)) {
        return 'This person has already been invited to this gallery.';
      }
      if (this.parentGalleryMembers.find(m => m.user['email']?.toLowerCase() === email)) {
        return 'This person has already been invited to the parent gallery.';
      }
      if (this.owner['email']?.toLowerCase() === email) {
        return 'This is the owner of this gallery. They already have access.';
      }
    };
  }

  getFilteredEmails() {
    if (this.emailsEntryField) {
      this.emailsCached = this.emailsEntryField.getEmails();
    }
    return this.emailsCached;
  }

  getNonMemberEmails() {
    return this.getFilteredEmails().filter(email => !this.members.some(m => m.email === email));
  }

  getMemberEmails() {
    return this.getFilteredEmails().filter(email => this.members.some(m => m.email === email));
  }

  canInviteToTeam() {
    if (!this.getNonMemberEmails().length) return false;
    return this.permissionService.hasRolePermission('invitations:create');
  }

  continue() {
    if (!this.canInviteToTeam()) {
      return this.sendInvites(this.getFilteredEmails(), []);
    }
    this.nonMemberEmails = this.getNonMemberEmails();
    this.inviteMode = 'choice';
    this.onChangeInviteMode.emit('choice');
  }

  sendInvitesWithChoice() {
    if (this.inviteType === 'gallery') {
      return this.sendInvites(this.getFilteredEmails(), []);
    }

    return this.sendInvites(this.getFilteredEmails(), this.getNonMemberEmails());
  }

  async sendInvites(galleryInviteEmails: string[], tenantInviteEmails: string[]) {
    if (this.sendingInvites) return;
    if (!galleryInviteEmails.length && !tenantInviteEmails.length) {
      return this.emailsEntryField?.parseEmails();
    }
    this.sendingInvites = true;

    if (this.emailsEntryField) {
      this.emailsEntryField.form.setValue({ emails: '' });
      this.emailsEntryField.emails = [];
    }

    forkJoinSafe([
      ...galleryInviteEmails.map(email => {
        // If we are also inviting this person to the tenant, we don't want to notify them twice
        const notify = tenantInviteEmails.find(e => e === email) ? false : this.messageForm.value.notify;
        return this.galleryMemberService.inviteMember(
          email,
          this.gallery.id,
          this.inviteAccessLevel,
          notify,
          (this.messageForm.value.message || '').split('\n').join('<br>')
        );
      }),
      ...tenantInviteEmails.map(email => from(this.tenantService.inviteUser(email, 'member'))),
    ])
      .pipe(
        delay(500), // giving the backend some time to automatically accept the invite
        switchMap(() =>
          forkJoin([
            this.tenantService.getMembers().pipe(first()),
            this.galleryMemberService.getGalleryMembers(this.gallery.id).pipe(first()),
            this.galleryMemberService.getPendingGalleryMembers(this.gallery.id).pipe(first()),
          ]).pipe(
            tap(([members, galleryMembers, pendingGalleryMembers]) => {
              this.members = members;
              this.galleryMembers = galleryMembers;
              this.pendingGalleryMembers = pendingGalleryMembers;
              this.messageForm.setValue({
                message: '',
                notify: true,
              });
              this.messageTyped = false;

              if (galleryInviteEmails.length || tenantInviteEmails.length)
                this.toasts.add('Invitations sent!', 'success');

              this.sendingInvites = false;
              this.inviteMode = null;
              this.onChangeInviteMode.emit(null);
            })
          )
        )
      )
      .subscribe();
  }

  memberIsGuest(member: GalleryMemberDto) {
    return !this.members.find(m => m.id === member.user['id']);
  }

  changeNotifyPeople() {
    this.analytics.track('toggle-switch', false, {
      name: 'notify_people',
      location: 'share-gallery-collaborators',
      value: this.messageForm.value.notify,
    });
  }

  onMessageChange() {
    if (this.messageTyped) return;
    this.messageTyped = true;
    this.analytics.track('added_message');
  }

  private async getUserGroupUserCounts(groups: GroupDto[]) {
    const results = await Promise.all(
      groups.map(async userGroup => {
        const resp = await this.userGroupService.getUserGroupUsers(userGroup.id);
        return resp.length;
      })
    );

    groups.forEach((userGroup, index) => {
      this.userGroupUserCounts[userGroup.id] = results[index];
    });
  }

  toggleUserGroupMenu(open: boolean) {
    if (open) {
      this.getUserGroupUserCounts(this.userGroups);
    }
  }

  async toggleUserGroup(group: GroupDto) {
    const checked = this.galleryUserGroupsMap[group.id];
    if (checked) {
      this.galleryUserGroupsMap[group.id] = false;
      await this.userGroupService.removeUserGroupFromGallery(group.id, this.gallery.id);
    } else {
      this.galleryUserGroupsMap[group.id] = true;
      const accessLevel =
        this.gallery.tenant_access === 'no_access'
          ? 'view_only'
          : (this.gallery.tenant_access as GalleryGroupAccessLevel);
      await this.userGroupService.addUserGroupToGallery(group.id, this.gallery.id, accessLevel);
    }
    this.galleryUserGroups = await this.getGalleryUserGroups();
    this.galleryUserGroups.forEach(g => {
      g['groupData'] = this.userGroups.find(ug => ug.id === g.group);
    });
  }

  ngOnDestroy() {
    this.updateSub?.unsubscribe();
  }
}
